// src/features/trips/components/TaleForm.js
import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
// import { PhotoAnnotator } from "../../../app/components/_PhotoAnnotator";
import { PhotoAnnotatorButton } from "../../../app/components/PhotoAnnotatorButton";

/**
 * Utility: convert ISO string (e.g. "2024-02-06T12:30:00.000Z")
 * to a value for <input type="datetime-local"> ("YYYY-MM-DDTHH:mm").
 */
function isoToLocalInputValue(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

/**
 * Utility: convert a "datetime-local" value ("YYYY-MM-DDTHH:mm")
 * to full ISO string (in UTC).
 */
function localInputValueToIso(localValue) {
  if (!localValue) return "";
  const date = new Date(localValue); // interpreted as local time
  date.setHours(0, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

// Help text per field
const HELP_TEXT = {
  title:
    "Give this trip a short, descriptive name (e.g., “Costa Rica 2024”). This field is required.",
  startDateTime:
    "Choose the date and time when this trip starts. You can leave it blank if you don’t know yet.",
  endDateTime:
    "Choose the date and time when this trip ends. It should be after the start date/time.",
  timeZone:
    "Use a valid IANA time zone like “America/Boise” or “America/Costa_Rica”. This is used for localizing your trip dates.",
  description:
    "Add a brief overview of the trip, important notes, or goals for the trip.",
  image:
    "Attach a representative image for this trip. You can upload from your device or use your camera/photos if supported.",
  travelers:
    "Add the names of people traveling on this trip. Use + to add more travelers and - to remove a row.",
};

/**
 * TaleForm
 *
 * Props:
 * - initialValue: {
 *     id?,                 // if editing an existing trip
 *     title,
 *     startDateTime,       // ISO string
 *     endDateTime,         // ISO string
 *     description,
 *     timeZone,
 *     image,               // string (e.g., base64 or file path)
 *     travelers: string[],
 *   }
 * - onSave(tripObj) -> Promise|void
 * - onCancel() -> void
 */
export default function TaleForm({ initialValue, onSave, onCancel }) {
  const [images, setImages] = useState([]); // kept images
  const { taleTitle } = useParams();
  const [formData, setFormData] = useState(() => {
    const base = initialValue || {
      title: taleTitle || "",
      startDateTime: "",
      endDateTime: "",
      description: "",
      timeZone: "America/Boise",
      image: "",
      travelers: [],
    };

    return {
      title: base.title || "",
      startDateTime: isoToLocalInputValue(base.startDateTime),
      endDateTime: isoToLocalInputValue(base.endDateTime),
      description: base.description || "",
      timeZone: base.timeZone || "America/Boise",
      image: base.image || "",
      travelers:
        Array.isArray(base.travelers) && base.travelers.length
          ? base.travelers
          : [""], // At least one blank row
    };
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Per-field help visibility
  const [showHelp, setShowHelp] = useState({
    title: false,
    startDateTime: false,
    endDateTime: false,
    timeZone: false,
    description: false,
    image: false,
    travelers: false,
  });

  const toggleHelp = (field) => {
    setShowHelp((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Image mode: "upload" or "camera"
  //   const [imageMode, setImageMode] = useState("upload");
  const [cameraPromptOpen, setCameraPromptOpen] = useState(false);
  //   const uploadInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Generic change handler for simple string fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleImagesChange = (nextImages) => {
    setImages(nextImages);
  };
  // Handle file selection from either upload or camera input
  //   const handleImageFromInput = (e) => {
  //     const file = e.target.files?.[0];
  //     if (!file) return;

  //     const reader = new FileReader();
  //     reader.onload = () => {
  //       setFormData((prev) => ({
  //         ...prev,
  //         image: reader.result || "",
  //       }));
  //     };
  //     reader.readAsDataURL(file);
  //   };

  // Travelers: add/remove/update
  const handleTravelerChange = (index, value) => {
    setFormData((prev) => {
      const nextTravelers = [...prev.travelers];
      nextTravelers[index] = value;
      return { ...prev, travelers: nextTravelers };
    });
  };

  const handleAddPhoto = async ({ blob, dataUrl, fileName }) => {
    // optional: upload blob, store in IndexedDB, attach to form, etc.
    console.log("Annotated image:", { blob, dataUrl, fileName });
  };

  const handleAddTraveler = () => {
    setFormData((prev) => ({
      ...prev,
      travelers: [...prev.travelers, ""],
    }));
  };

  const handleRemoveTraveler = (index) => {
    setFormData((prev) => {
      const nextTravelers = prev.travelers.filter((_, i) => i !== index);
      return {
        ...prev,
        travelers: nextTravelers.length ? nextTravelers : [""],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Title is required
    if (!formData.title.trim()) {
      setError("Title is required.");
      return;
    }

    // Convert datetime-local values to ISO strings
    const startIso = localInputValueToIso(formData.startDateTime);
    const endIso = localInputValueToIso(formData.endDateTime);

    if (startIso && endIso && new Date(startIso) > new Date(endIso)) {
      setError("Start date/time must be before end date/time.");
      return;
    }

    // Strip empty travelers
    const travelersClean = formData.travelers
      .map((t) => t.trim())
      .filter(Boolean);

    const payload = {
      title: formData.title.trim(),
      startDateTime: startIso,
      endDateTime: endIso,
      description: formData.description.trim(),
      timeZone: formData.timeZone.trim(),
      image: formData.image, // already a string
      travelers: travelersClean,
    };

    try {
      setSaving(true);
      await onSave?.(payload);
    } catch (err) {
      console.error(err);
      setError("Something went wrong while saving. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Small set of suggested IANA zones
  const TIMEZONES = [
    "America/Boise",
    "America/Los_Angeles",
    "America/Denver",
    "America/Chicago",
    "America/New_York",
    "America/Costa_Rica",
    "Europe/London",
    "Europe/Paris",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];

  // Called when user confirms camera/photo access in our custom prompt.
  const handleConfirmCameraPrompt = () => {
    setCameraPromptOpen(false);

    // Trigger the hidden camera/photo input.
    // The browser/OS will show any real permission dialog.
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  return (
    <>
      {/* CAMERA PERMISSION PROMPT (simple custom modal) */}
      {cameraPromptOpen && (
        <>
          <div className="modal d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Camera / Photos Permission</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setCameraPromptOpen(false)}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    To use your camera or photo library, your browser or device
                    may ask for permission. If prompted, please allow access to
                    photos/camera, then choose the image you want to use for
                    this trip.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setCameraPromptOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmCameraPrompt}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Simple backdrop */}
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      <form className="container mt-3" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-8">
            {/* Title (REQUIRED) */}
            <div className="mb-3">
              <label htmlFor="title" className="form-label">
                Title <span className="text-danger">*</span>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-1 align-baseline text-muted"
                  onClick={() => toggleHelp("title")}
                  aria-label="Title field help"
                >
                  ⓘ
                </button>
              </label>
              <input
                id="title"
                name="title"
                type="text"
                className="form-control"
                value={formData.title}
                onChange={handleChange}
                placeholder="Costa Rica 2024"
                required
              />
              {showHelp.title && (
                <div className="form-text">{HELP_TEXT.title}</div>
              )}
            </div>

            {/* Start / End DateTime */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="startDateTime" className="form-label">
                  Start Date &amp; Time
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1 align-baseline text-muted"
                    onClick={() => toggleHelp("startDateTime")}
                    aria-label="Start date/time help"
                  >
                    ⓘ
                  </button>
                </label>
                <input
                  id="startDateTime"
                  name="startDateTime"
                  type="date"
                  className="form-control"
                  value={formData.startDateTime}
                  onChange={handleChange}
                />
                {showHelp.startDateTime && (
                  <div className="form-text">{HELP_TEXT.startDateTime}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="endDateTime" className="form-label">
                  End Date &amp; Time
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1 align-baseline text-muted"
                    onClick={() => toggleHelp("endDateTime")}
                    aria-label="End date/time help"
                  >
                    ⓘ
                  </button>
                </label>
                <input
                  id="endDateTime"
                  name="endDateTime"
                  type="date"
                  className="form-control"
                  value={formData.endDateTime}
                  onChange={handleChange}
                />
                {showHelp.endDateTime && (
                  <div className="form-text">{HELP_TEXT.endDateTime}</div>
                )}
              </div>
            </div>

            {/* Time Zone (IANA) with typeahead (datalist) */}
            <div className="mb-3">
              <label htmlFor="timeZone" className="form-label">
                Time Zone (IANA)
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-1 align-baseline text-muted"
                  onClick={() => toggleHelp("timeZone")}
                  aria-label="Time zone help"
                >
                  ⓘ
                </button>
              </label>
              <input
                id="timeZone"
                name="timeZone"
                type="text"
                className="form-control"
                list="timezoneOptions"
                placeholder="America/Boise"
                value={
                  formData.timeZone ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone ||
                  ""
                }
                onChange={handleChange}
              />
              <datalist id="timezoneOptions">
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
              {showHelp.timeZone && (
                <div className="form-text">{HELP_TEXT.timeZone}</div>
              )}
            </div>

            {/* Description */}
            <div className="mb-3">
              <label htmlFor="description" className="form-label">
                Description
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-1 align-baseline text-muted"
                  onClick={() => toggleHelp("description")}
                  aria-label="Description help"
                >
                  ⓘ
                </button>
              </label>
              <textarea
                id="description"
                name="description"
                rows="4"
                className="form-control"
                value={formData.description}
                onChange={handleChange}
                placeholder="Trip overview, goals, highlights..."
              />
              {showHelp.description && (
                <div className="form-text">{HELP_TEXT.description}</div>
              )}
            </div>

            {/* Image upload / camera */}
            {/* <PhotoAnnotator /> */}
            {/* <div className="mb-3">
              <label className="form-label" htmlFor="imageModeUpload">
                Trip Image
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-1 align-baseline text-muted"
                  onClick={() => toggleHelp("image")}
                  aria-label="Image field help"
                >
                  ⓘ
                </button>
              </label>

              {/* Mode selection *
              <div className="mb-2">
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="imageMode"
                    id="imageModeUpload"
                    value="upload"
                    checked={imageMode === "upload"}
                    onChange={() => setImageMode("upload")}
                  />
                  <label className="form-check-label" htmlFor="imageModeUpload">
                    Upload file
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="imageMode"
                    id="imageModeCamera"
                    value="camera"
                    checked={imageMode === "camera"}
                    onChange={() => setImageMode("camera")}
                  />
                  <label className="form-check-label" htmlFor="imageModeCamera">
                    Use camera / photos
                  </label>
                </div>
              </div>

              {/* Upload mode *
              {imageMode === "upload" && (
                <div className="mb-2">
                  <input
                    id="image"
                    name="image"
                    type="file"
                    accept="image/*"
                    className="form-control"
                    ref={uploadInputRef}
                    onChange={handleImageFromInput}
                  />
                </div>
              )}

              {/* Camera mode *
              {imageMode === "camera" && (
                <div className="mb-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setCameraPromptOpen(true)}
                  >
                    Choose from camera/photos
                  </button>

                  {/* Hidden input that will be triggered after confirming the prompt.
                      `capture="environment"` hints to use the rear camera on mobile devices. *
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    ref={cameraInputRef}
                    onChange={handleImageFromInput}
                  />
                </div>
              )}

              {showHelp.image && (
                <div className="form-text">{HELP_TEXT.image}</div>
              )}

              {/* Preview *
              {formData.image && (
                <div className="mt-2">
                  <img
                    src={formData.image}
                    alt="Trip preview"
                    style={{ maxWidth: "200px", maxHeight: "150px" }}
                    className="img-thumbnail"
                  />
                </div>
              )}
            </div> */}

            {/* Travelers */}
            <div className="mb-3">
              <label className="form-label">
                Travelers
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-1 align-baseline text-muted"
                  onClick={() => toggleHelp("travelers")}
                  aria-label="Travelers field help"
                >
                  ⓘ
                </button>
              </label>
              {showHelp.travelers && (
                <div className="form-text">{HELP_TEXT.travelers}</div>
              )}

              {formData.travelers.map((traveler, index) => (
                <div className="input-group mb-2" key={index}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Traveler name"
                    value={traveler}
                    onChange={(e) =>
                      handleTravelerChange(index, e.target.value)
                    }
                  />
                  {/* Remove (-) button */}
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => handleRemoveTraveler(index)}
                    disabled={
                      formData.travelers.length === 1 &&
                      !formData.travelers[0].trim()
                    }
                  >
                    &minus;
                  </button>
                  {/* Add (+) button on the last row */}
                  {index === formData.travelers.length - 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddTraveler}
                    >
                      +
                    </button>
                  )}
                </div>
              ))}
            </div>
            <PhotoAnnotatorButton
              label="Choose or take a photo"
              onSave={handleAddPhoto}
              captureCameraOnly={false}
              onImagesChange={handleImagesChange}
            />
            {/* Error message */}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
